/**
 * A janela de construção do stage no cartão do ajuste livre (spec
 * 2026-11-03-janela-de-construcao-do-stage-design D7): o selo, por que o pedido
 * esperou um Sênior mesmo com a janela aberta, e os botões Fechar/Reabrir.
 *
 * Quem pode fechar ou reabrir é o servidor (Sênior do tenant, §9.2): o painel
 * mostra os botões e devolve o motivo do 403/409 quando recusa. Reabrir exige
 * motivo de 30 caracteres, como o servidor.
 */
import { useState } from 'react'
import { Badge, Button, Group, Modal, Stack, Text, Textarea } from '@mantine/core'
import { useMutation } from '@tanstack/react-query'
import { fecharJanelaDoStage, reabrirJanelaDoStage, type JanelaDoPedido } from '@/api/terraformRunner'
import { motivoDoErro } from './DecisaoDoPedido'

const BARLOW = '"Barlow", Arial, sans-serif'
const MOTIVO_MINIMO = 30

const POR_QUE: Record<string, string> = {
  superficie_sensivel: 'o plan toca IAM, DNS, certificado, WAF ou security group',
  plano_com_destrutivas: 'o plan destrói ou recria recurso',
  plano_com_expostos: 'o plan expõe recurso ao mundo',
  plano_com_afrouxa: 'o plan afrouxa o WAF',
  plano_com_inesperadas: 'o plan tem efeito inesperado',
  plano_com_deriva: 'o plan tem deriva',
  nao_e_senior: 'quem pediu não é DevOps Sênior',
}

function data(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('pt-BR') : '—'
}

export function JanelaDeConstrucao({ janela, onMudou }: {
  janela: JanelaDoPedido
  onMudou: () => void
}) {
  const [reabrindo, setReabrindo] = useState(false)
  const [motivo, setMotivo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const pronto = () => { setReabrindo(false); setMotivo(''); setErro(null); onMudou() }
  const fechar = useMutation({
    mutationFn: () => fecharJanelaDoStage(janela.tenant_id),
    onSuccess: pronto,
    onError: (err) => setErro(motivoDoErro(err)),
  })
  const reabrir = useMutation({
    mutationFn: () => reabrirJanelaDoStage(janela.tenant_id, motivo.trim()),
    onSuccess: pronto,
    onError: (err) => setErro(motivoDoErro(err)),
  })
  const porQue = POR_QUE[janela.motivo]

  return (
    <Stack gap={4} mt="xs">
      <Group gap={6}>
        <Badge color={janela.aberta ? 'inovareTeal' : 'gray'} variant="light">
          Janela de construção
        </Badge>
        <Text size="sm" style={{ fontFamily: BARLOW }}>
          {janela.aberta ? `aberta até ${data(janela.expira_em)}`
            : janela.estado === 'expirada' ? `expirou em ${data(janela.expira_em)}`
              : janela.estado === 'sem_janela' ? 'não aberta para este stage' : 'fechada'}
        </Text>
      </Group>
      {janela.aberta && janela.dispensou && (
        <Text size="sm" style={{ fontFamily: BARLOW }}>
          Plan sem destruição, exposição nem superfície sensível: segue sem esperar o DevOps.
        </Text>
      )}
      {janela.aberta && !janela.dispensou && porQue && (
        <Text size="sm" style={{ fontFamily: BARLOW }}>
          A janela está aberta, mas {porQue}: precisa de um Sênior.
          {janela.sensiveis.length > 0 && ` (${janela.sensiveis.join(', ')})`}
        </Text>
      )}
      <Group gap="xs">
        {janela.aberta && (
          <Button size="xs" variant="light" loading={fechar.isPending}
                  onClick={() => { setErro(null); fechar.mutate() }}>
            Fechar janela (stage em uso)
          </Button>
        )}
        {!janela.aberta && janela.pode_reabrir && (
          <Button size="xs" variant="light" onClick={() => { setErro(null); setReabrindo(true) }}>
            Reabrir janela
          </Button>
        )}
      </Group>
      {erro && !reabrindo && <Text size="sm" c="red">{erro}</Text>}
      <Modal opened={reabrindo} onClose={() => setReabrindo(false)} title="Reabrir a janela de construção">
        <Stack gap="xs">
          <Text size="sm" style={{ fontFamily: BARLOW }}>
            Reabre por 14 dias a partir de agora. Só um DevOps Sênior reabre, e só stage criado pelo runner.
          </Text>
          <Textarea label="Motivo" minRows={3} value={motivo}
                    onChange={(e) => setMotivo(e.currentTarget.value)}
                    description={`Pelo menos ${MOTIVO_MINIMO} caracteres.`} />
          {erro && <Text size="sm" c="red">{erro}</Text>}
          <Group justify="flex-end">
            <Button size="xs" loading={reabrir.isPending}
                    disabled={motivo.trim().length < MOTIVO_MINIMO}
                    onClick={() => reabrir.mutate()}>
              Reabrir
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
